import React, { useEffect, useRef, useState } from 'react';
import { IoDesktopOutline } from 'react-icons/io5';
import Modal from './Modal';
import { useLanguage } from '../i18n/LanguageContext';

// Entirely main-process-driven: electron-starter.js's setDisplayMediaRequestHandler
// sends 'open-screen-picker' whenever it needs a source it can't resolve on
// its own (macOS fallback, Windows, X11 - see that file's comments for which
// platforms actually reach this vs. get a native OS picker instead), and
// this is the only thing that ever flips `show`/populates `sources` - there
// is no other trigger. window.cyclone doesn't exist in a plain browser tab,
// so this component is inert there (correct: getDisplayMedia's own native
// browser picker handles it instead).
export default function ScreenSharePicker() {
  const { t } = useLanguage();
  const [show, setShow] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [sources, setSources] = useState([]);
  const [tab, setTab] = useState('screen');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!window.cyclone || !window.cyclone.onOpenScreenPicker) return undefined;
    return window.cyclone.onOpenScreenPicker(async ({ requestId: id }) => {
      setRequestId(id);
      setSources([]);
      setTab('screen');
      setLoading(true);
      setShow(true);
      const list = await window.cyclone.getScreenSources();
      setLoading(false);
      setSources(list);
    });
  }, []);

  const choose = (sourceId) => {
    if (requestId != null) window.cyclone.resolveScreenPick(requestId, sourceId);
    setRequestId(null);
    setShow(false);
  };

  // Modal's own close paths (Escape, backdrop click) only flip `show` -
  // they have no idea a main-process Promise is waiting on an answer. Left
  // unresolved, electron-starter.js's promptRendererForSource() would hang
  // forever and getDisplayMedia() would never settle on the caller's side.
  // Catches that close by watching the same falling edge Modal itself acts
  // on, and answers it as a cancellation (null) exactly like the explicit
  // "Cancel" button below does.
  const prevShow = useRef(show);
  useEffect(() => {
    if (prevShow.current && !show && requestId != null) choose(null);
    prevShow.current = show;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const screens = sources.filter((s) => s.id.startsWith('screen:'));
  const windows = sources.filter((s) => s.id.startsWith('window:'));
  const visible = tab === 'screen' ? screens : windows;

  return (
    <Modal mState={{ iShow: show, setiShow: setShow }} buttonShow>
      <h4 style={{ marginTop: 0 }}>{t('voice.screenPickerTitle')}</h4>
      <div className="ScreenPickerTabs">
        <button className={tab === 'screen' ? 'primaryColor' : 'secondaryColor'} onClick={() => setTab('screen')}>
          {t('voice.screenPickerScreens')} {screens.length > 0 ? `(${screens.length})` : ''}
        </button>
        <button className={tab === 'window' ? 'primaryColor' : 'secondaryColor'} onClick={() => setTab('window')}>
          {t('voice.screenPickerWindows')} {windows.length > 0 ? `(${windows.length})` : ''}
        </button>
      </div>
      {loading ? (
        <p className="descGray">{t('voice.screenPickerLoading')}</p>
      ) : (
        <div className="ScreenPickerGrid">
          {visible.map((s) => (
            <button key={s.id} className="ScreenPickerItem" onClick={() => choose(s.id)} title={s.name}>
              {s.thumbnailDataUrl ? <img src={s.thumbnailDataUrl} alt={s.name} /> : <IoDesktopOutline className="ScreenPickerItemFallback" />}
              <span>{s.name}</span>
            </button>
          ))}
          {visible.length === 0 && <p className="descGray">{t('voice.screenPickerEmpty')}</p>}
        </div>
      )}
      {window.cyclone && window.cyclone.platform === 'darwin' && (
        <p className="descGray ScreenPickerNote">{t('voice.screenPickerMacAudioNote')}</p>
      )}
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <button onClick={() => choose(null)} className="SendButton secondaryColor">{t('common.cancel')}</button>
      </div>
    </Modal>
  );
}
